import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { UserPlus, Trash2, Users, Briefcase, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AssignmentsTab = ({ institutionId }) => {
  const [assignments, setAssignments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [formUserId, setFormUserId] = useState('');
  const [formPositionId, setFormPositionId] = useState('');
  const [formUnitId, setFormUnitId] = useState('');
  const [formScope, setFormScope] = useState('self');
  const [formPrimary, setFormPrimary] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [aRes, pRes, uRes] = await Promise.all([
          axios.get(`${API}/core/institutions/${institutionId}/assignments`),
          axios.get(`${API}/core/institutions/${institutionId}/positions`),
          axios.get(`${API}/core/institutions/${institutionId}/units`),
        ]);
        setAssignments(aRes.data); setPositions(pRes.data); setUnits(uRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [institutionId]);

  const searchUsers = async (q) => {
    setUserSearch(q);
    if (q.length < 2) { setUserResults([]); return; }
    try {
      const { data } = await axios.get(`${API}/admin/users?search=${encodeURIComponent(q)}&limit=10`);
      setUserResults(data.users || data || []);
    } catch (e) { setUserResults([]); }
  };

  const createAssignment = async () => {
    try {
      await axios.post(`${API}/core/institutions/${institutionId}/assignments`, {
        user_id: formUserId, position_id: formPositionId,
        organizational_unit_id: formUnitId, scope: formScope, is_primary: formPrimary
      });
      toast.success('Affectation creee');
      setShowCreate(false);
      const { data } = await axios.get(`${API}/core/institutions/${institutionId}/assignments`);
      setAssignments(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Desactiver cette affectation ?')) return;
    try {
      await axios.delete(`${API}/core/institutions/${institutionId}/assignments/${id}`);
      toast.success('Affectation desactivee');
      setAssignments(assignments.filter(a => a.id !== id));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="flex items-center justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="assignments-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">{assignments.length} affectation(s)</p>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-assignment-btn"><UserPlus className="w-4 h-4 mr-1" /> Affecter</Button>
      </div>
      <div className="space-y-2">
        {assignments.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-gray-400 text-sm">Aucune affectation.</CardContent></Card>
        ) : assignments.map(a => (
          <Card key={a.id} data-testid={`assignment-${a.id}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0"><Users className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{a.user?.first_name} {a.user?.last_name} <span className="text-gray-400 font-normal">({a.user?.email})</span></p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {a.position && <Badge variant="secondary" className="text-[10px]"><Briefcase className="w-3 h-3 mr-1" />{a.position.name_fr}</Badge>}
                  {a.unit && <Badge variant="outline" className="text-[10px]"><Building2 className="w-3 h-3 mr-1" />{a.unit.name}</Badge>}
                  <Badge variant="outline" className="text-[10px]">{a.scope}</Badge>
                  {a.is_primary && <Badge className="text-[10px] bg-green-100 text-green-700">Principal</Badge>}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => deactivate(a.id)}><Trash2 className="w-4 h-4 text-red-400" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg" data-testid="create-assignment-dialog">
          <DialogHeader><DialogTitle>Nouvelle Affectation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Utilisateur *</Label><Input value={userSearch} onChange={e => searchUsers(e.target.value)} placeholder="Rechercher par nom ou email..." />
              {userResults.length > 0 && <div className="border rounded-md mt-1 max-h-32 overflow-y-auto">
                {userResults.map(u => <button key={u.id} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                  onClick={() => { setFormUserId(u.id); setUserSearch(`${u.first_name} ${u.last_name}`); setUserResults([]); }}>
                  {u.first_name} {u.last_name} <span className="text-gray-400">({u.role})</span></button>)}
              </div>}</div>
            <div><Label>Poste</Label>
              <Select value={formPositionId} onValueChange={setFormPositionId}><SelectTrigger><SelectValue placeholder="Choisir un poste" /></SelectTrigger>
                <SelectContent>{positions.map(p => <SelectItem key={p.id} value={p.id}>{p.name_fr}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Structure</Label>
              <Select value={formUnitId} onValueChange={setFormUnitId}><SelectTrigger><SelectValue placeholder="Choisir une structure" /></SelectTrigger>
                <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Perimetre</Label>
              <Select value={formScope} onValueChange={setFormScope}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Personnel</SelectItem><SelectItem value="department">Departement</SelectItem>
                  <SelectItem value="faculty">Faculte</SelectItem><SelectItem value="institution">Institution</SelectItem>
                </SelectContent></Select></div>
            <div className="flex items-center gap-2"><Switch checked={formPrimary} onCheckedChange={setFormPrimary} /><Label className="mb-0">Affectation principale</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button><Button onClick={createAssignment} disabled={!formUserId}>Affecter</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
